import { Controller } from "@hotwired/stimulus"
import { chart } from "../gate"
import { addMonths, today } from "../backtest"

const LABELS = { 1: "a month ago", 12: "a year ago", 60: "5 years ago", 120: "10 years ago" }

export default class extends Controller {
  static targets = ["canvas", "range", "invested"]
  static values = { symbol: String, name: String }

  async connect() {
    try {
      this.prices = await chart(this.symbolValue)
    } catch {
      this.element.hidden = true
      return
    }

    this.money = new Intl.NumberFormat("en-GB", { style: "currency", currency: this.prices.currency, maximumFractionDigits: 0 })
    this.price = new Intl.NumberFormat("en-GB", { style: "currency", currency: this.prices.currency, maximumFractionDigits: 2 })
    this.draw(12)
  }

  disconnect() {
    this.chart?.destroy()
  }

  select({ currentTarget, params: { months } }) {
    this.rangeTargets.forEach((range) => range.classList.toggle("news__range--active", range === currentTarget))
    this.draw(months)
  }

  draw(months) {
    const start = addMonths(today(), -months)
    const rows = this.prices.dates.map((date, index) => [date, this.prices.closes[index]]).filter(([date, close]) => date >= start && close != null)
    if (rows.length < 2) return

    const first = rows[0][1]
    const last = rows[rows.length - 1][1]
    const worth = (1000 * last) / first
    const change = (last / first - 1) * 100
    const tone = change < 0 ? "calculator-down" : "calculator-up"

    const amount = document.createElement("strong")
    amount.textContent = this.money.format(worth)
    const percent = document.createElement("span")
    percent.className = tone
    percent.textContent = `${change < 0 ? "−" : "+"}${Math.abs(change).toFixed(1)}%`
    this.investedTarget.replaceChildren(`${this.money.format(1000)} in ${this.nameValue} ${LABELS[months]} would be worth `, amount, " today ", percent)

    this.chart?.destroy()
    this.chart = new window.Chart(this.canvasTarget.getContext("2d"), {
      type: "line",
      data: {
        labels: rows.map(([date]) => date),
        datasets: [{
          data: rows.map(([, close]) => close),
          borderColor: change < 0 ? "#d048b6" : "#00d6b4",
          borderWidth: 2,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 4,
          lineTension: 0
        }]
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        animation: false,
        legend: { display: false },
        hover: { mode: "index", intersect: false },
        tooltips: {
          mode: "index",
          intersect: false,
          displayColors: false,
          backgroundColor: "#fff",
          titleFontColor: "#333",
          bodyFontColor: "#666",
          xPadding: 12,
          callbacks: { label: (item) => this.price.format(item.yLabel) }
        },
        scales: {
          yAxes: [{
            gridLines: { drawBorder: false, color: "rgba(255, 255, 255, 0.06)", zeroLineColor: "rgba(255, 255, 255, 0.06)" },
            ticks: { maxTicksLimit: 5, padding: 8, fontColor: "#9a9a9a", callback: (value) => this.price.format(value) }
          }],
          xAxes: [{
            gridLines: { display: false, drawBorder: false },
            ticks: { maxTicksLimit: 6, maxRotation: 0, padding: 8, fontColor: "#9a9a9a" }
          }]
        }
      }
    })
  }
}
